// Administrare → Semnătură electronică: institution name and signing options, the DocFlow certification authority,
// and the users' signing certificates.
import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Confirm, Field, fmtDate, fmtDateTime, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';

interface Ca { id: number; name: string; active: boolean; createdAt: string; validTo: string; fingerprint: string; certificates: number }
interface Cert { id: number; userId: number; subject: string; serial: string; caId?: number; createdAt: string; validTo: string; revokedAt?: string | null; used: number; lastUsed?: string | null }
interface AdminData {
  settings: { org: string; location: string; page: 'last' | 'first' }; configured: boolean; cas: Ca[]; certificates: Cert[];
  totals: { signatures: number; documents: number; signers: number; last?: string | null };
}
const fixTs = (iso?: string | null) => (iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);

export function SignatureAdminPage() {
  const toast = useToast();
  const [d, setD] = useState<AdminData | null>(null);
  const [f, setF] = useState<AdminData['settings'] | null>(null);
  const [busy, setBusy] = useState(false);
  const [renew, setRenew] = useState(false);
  const [revoke, setRevoke] = useState<Cert | null>(null);
  const load = () => api.get<AdminData>('/api/documents/sign/admin').then(x => { setD(x); setF(x.settings); }).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, []);
  if (!d || !f) return <p className="muted">Se încarcă…</p>;
  const dirty = JSON.stringify(f) !== JSON.stringify(d.settings);
  const active = d.cas.find(c => c.active);
  const renamed = active && d.settings.org && !active.name.endsWith('— ' + d.settings.org.trim());
  const save = async () => {
    setBusy(true);
    try { await api.put('/api/documents/sign/settings', f); toast('Setările semnăturii au fost salvate', 'ok'); await load(); }
    catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  const doRenew = async () => {
    setRenew(false);
    try { await api.post('/api/documents/sign/ca/renew', {}); toast('A fost creată o autoritate de certificare nouă', 'ok'); load(); } catch (e: any) { toast(e.message, 'error'); }
  };
  const doRevoke = async () => {
    const c = revoke!; setRevoke(null);
    try { await api.post(`/api/documents/sign/certs/${c.id}/revoke`, {}); toast('Certificatul a fost revocat', 'ok'); load(); } catch (e: any) { toast(e.message, 'error'); }
  };
  const downloadCa = async () => {
    const r = await fetch(api.authUrl('/api/documents/sign/ca.crt'));
    if (!r.ok) { toast('Certificatul nu a putut fi descărcat', 'error'); return; }
    const a = document.createElement('a'); a.href = URL.createObjectURL(await r.blob()); a.download = 'DocFlow-CA.crt'; document.body.appendChild(a); a.click(); a.remove();
  };
  return (
    <div className="stack" style={{ gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 18 }}>Semnătură electronică</h2>
        <p className="muted small">Utilizatorii semnează documentele cu butonul <b>Semnează</b>, confirmând cu parola. DocFlow emite fiecărui utilizator un certificat de semnătură, sub autoritatea de certificare a instituției.</p>
      </div>
      {!d.configured && <div className="banner warn"><Icon name="alert" size={16} /><span><b>Semnătura nu este încă activă.</b> Completați numele instituției și salvați — apoi utilizatorii pot semna.</span></div>}
      <div className="sig-stats">
        <div><b>{d.totals.signatures}</b><span>semnături aplicate</span></div>
        <div><b>{d.totals.documents}</b><span>documente semnate</span></div>
        <div><b>{d.totals.signers}</b><span>semnatari</span></div>
        <div><b>{d.totals.last ? fmtDate(fixTs(d.totals.last)) : '—'}</b><span>ultima semnătură</span></div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-head"><Icon name="settings" /><h2>Instituția și ștampila</h2></div>
          <div className="card-body stack" style={{ gap: 12 }}>
            <Field label="Numele instituției" required hint="Apare în certificate („DocFlow CA — …”) și pe fiecare semnătură.">
              <input className="input" value={f.org} onChange={e => setF({ ...f, org: e.target.value })} placeholder="ex. Primăria Municipiului Exemplu" maxLength={120} />
            </Field>
            <Field label="Locul semnării (opțional)" hint="Implicit numele instituției; ex. „Cluj-Napoca”.">
              <input className="input" value={f.location} onChange={e => setF({ ...f, location: e.target.value })} />
            </Field>
            <Field label="Ștampila, implicit pe">
              <div className="segmented"><button type="button" className={f.page === 'last' ? 'active' : ''} onClick={() => setF({ ...f, page: 'last' })}>Ultima pagină</button><button type="button" className={f.page === 'first' ? 'active' : ''} onClick={() => setF({ ...f, page: 'first' })}>Prima pagină</button></div>
            </Field>
            <div className="row"><button className="btn primary" disabled={!dirty || busy || !f.org.trim()} onClick={save}><Icon name="check" />Salvează</button></div>
            {renamed && (
              <div className="banner small warn"><Icon name="info" size={15} />
                <span>Autoritatea de certificare actuală poartă numele <b>{active!.name}</b>. Pentru ca semnăturile noi să poarte numele nou, creați o autoritate nouă — semnăturile existente rămân valide.
                  <div style={{ marginTop: 6 }}><button className="btn sm" onClick={() => setRenew(true)}><Icon name="refresh" />Creează autoritate nouă</button></div></span>
              </div>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-head"><Icon name="shield" /><h2>Autoritatea de certificare</h2><span className="grow" />{active && <button className="btn sm" onClick={downloadCa}><Icon name="download" />Certificatul CA</button>}</div>
          <div className="card-body stack" style={{ gap: 10 }}>
            {!active ? <p className="muted small" style={{ margin: 0 }}>Se creează automat la prima semnătură, cu numele instituției de mai sus (RSA 3072, valabilă 20 de ani).</p> : (
              <>
                <dl className="kv">
                  <dt>Nume</dt><dd><b>{active.name}</b></dd>
                  <dt>Creată</dt><dd>{fmtDateTime(fixTs(active.createdAt))}</dd>
                  <dt>Valabilă până</dt><dd>{fmtDate(active.validTo)}</dd>
                  <dt>Amprentă SHA-256</dt><dd className="mono small" style={{ overflowWrap: 'anywhere' }}>{active.fingerprint}…</dd>
                  <dt>Certificate emise</dt><dd>{active.certificates}</dd>
                </dl>
                {d.cas.length > 1 && <div className="muted small">Autorități anterioare (păstrate pentru verificarea semnăturilor vechi): {d.cas.filter(c => !c.active).map(c => c.name + ' · ' + fmtDate(fixTs(c.createdAt))).join('; ')}</div>}
              </>
            )}
            <div className="banner small"><Icon name="info" size={15} /><span>Pentru ca Adobe Reader să arate semnăturile ca valide, instalați certificatul CA pe calculatoarele care deschid documente semnate: Adobe → Preferințe → Semnături → Identități și certificate de încredere → Importă, bifând „Utilizează ca rădăcină de încredere”.</span></div>
          </div>
        </div>
        <div className="card span-2">
          <div className="card-head"><Icon name="users" /><h2>Certificatele utilizatorilor</h2><span className="grow" /><span className="muted small">emise automat la prima semnătură; reemise când se schimbă numele sau compartimentul</span></div>
          {d.certificates.length === 0 ? <div className="card-body"><span className="muted">Niciun certificat emis încă.</span></div> : (
            <div className="table-wrap" style={{ border: 0 }}>
              <table className="table">
                <thead><tr><th>Titular</th><th>Serie</th><th>Emis</th><th>Valabil până</th><th>Semnături</th><th>Ultima folosire</th><th>Stare</th><th /></tr></thead>
                <tbody>{d.certificates.map(c => (
                  <tr key={c.id} className={c.revokedAt ? 'inactive' : ''}>
                    <td>{c.subject.split(' | ')[0]}{c.subject.includes(' | ') && <div className="muted small">{c.subject.split(' | ')[1]}</div>}</td>
                    <td className="mono small">{c.serial}</td>
                    <td className="small">{fmtDate(fixTs(c.createdAt))}</td>
                    <td className="small">{fmtDate(c.validTo)}</td>
                    <td className="num">{c.used}</td>
                    <td className="small">{c.lastUsed ? fmtDateTime(fixTs(c.lastUsed)) : '—'}</td>
                    <td>{c.revokedAt ? <span className="tag">înlocuit / revocat</span> : <span className="tag adm-tag-ok">activ</span>}</td>
                    <td style={{ textAlign: 'right' }}>{!c.revokedAt && <button className="btn sm ghost" onClick={() => setRevoke(c)} title="Certificatul nu mai poate fi folosit; la următoarea semnătură se emite unul nou">Revocă</button>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card span-2">
          <div className="card-head"><Icon name="info" /><h2>Despre semnătură</h2></div>
          <div className="card-body small stack" style={{ gap: 6 }}>
            <span>• Semnătură electronică <b>avansată</b> PAdES: PDF semnat cu certificatul utilizatorului, cu ștampilă vizibilă (nume, compartiment, dată, motiv). Orice modificare ulterioară a fișierului invalidează semnătura.</span>
            <span>• Mai multe persoane pot semna același document, una după alta; fiecare semnătură creează o versiune nouă, iar semnăturile anterioare rămân valide.</span>
            <span>• Cheile private sunt păstrate criptat pe serverul DocFlow; fiecare semnătură se confirmă cu parola utilizatorului.</span>
            <span>• Pentru actele care cer semnătură electronică <b>calificată</b> (eIDAS) se folosește certificatul calificat (token certSIGN, DigiSign…); PDF-ul semnat se încarcă drept versiune nouă, iar DocFlow îi verifică semnăturile.</span>
          </div>
        </div>
      </div>
      {renew && <Confirm title="Autoritate de certificare nouă" text={<>Se creează <b>DocFlow CA — {d.settings.org}</b>. Utilizatorii primesc certificate noi la următoarea semnătură. Semnăturile existente rămân valide în DocFlow; în Adobe Reader trebuie instalat și noul certificat CA.</>} yesLabel="Creează" onYes={doRenew} onNo={() => setRenew(false)} />}
      {revoke && <Confirm title="Revocare certificat" text={<>Certificatul lui <b>{revoke.subject.split(' | ')[0]}</b> nu va mai fi folosit. La următoarea semnătură i se emite unul nou. Semnăturile deja făcute rămân în documente.</>} danger yesLabel="Revocă" onYes={doRevoke} onNo={() => setRevoke(null)} />}
    </div>
  );
}
